// Ocean — self-contained Gerstner wave renderer
// Engine feature: opt-in. Game creates an Ocean and calls draw() in the G-buffer pass.
// Does not touch deferred.ts — manages its own pipeline, mesh, and uniforms.

import { DEPTH_FORMAT, type GPU } from "./gpu.js";
import { GBUFFER_TARGETS, POS_NORM_LAYOUT } from "./deferred.js";

const GRID_SIZE = 256;
const WORLD_SIZE = 2000;

const OCEAN_SHADER = /* wgsl */ `
struct OceanUniforms {
  viewProj: mat4x4f,
  time: f32, _pad: f32, _pad2: f32, _pad3: f32,
  deepColor: vec3f, _pc0: f32,
  midColor: vec3f, _pc1: f32,
  crestColor: vec3f, _pc2: f32,
};

@group(0) @binding(0) var<uniform> ocean: OceanUniforms;

struct WaveParams { dir: vec2f, amplitude: f32, frequency: f32, steepness: f32, speed: f32 };

// 14 octaves: large swells → sharp medium waves → omnidirectional ripples
// High steepness on mid-frequency waves creates defined ridge lines
// Speeds kept slow for calm, majestic ocean feel
const WAVES = array<WaveParams, 14>(
  // Large swells — slow, gentle movement
  WaveParams(vec2f(1.0, 0.0),    0.8,  0.15, 0.5,  0.4),
  WaveParams(vec2f(0.7, 0.7),    0.5,  0.25, 0.45, 0.3),
  // Medium waves — sharp ridges, moderate speed
  WaveParams(vec2f(-0.3, 0.9),   0.35, 0.4,  0.7,  0.5),
  WaveParams(vec2f(0.5, -0.8),   0.2,  0.6,  0.65, 0.7),
  WaveParams(vec2f(0.9, 0.3),    0.15, 0.9,  0.6,  0.6),
  WaveParams(vec2f(-0.6, -0.7),  0.1,  1.2,  0.55, 0.8),
  // Small detail — slightly faster relative to wavelength (natural)
  WaveParams(vec2f(-0.8, 0.4),   0.06, 1.8,  0.45, 1.0),
  WaveParams(vec2f(0.3, -0.6),   0.04, 3.0,  0.35, 1.2),
  // Cross-directional ripples — break up the corduroy pattern
  WaveParams(vec2f(0.9, -0.4),   0.03, 3.5,  0.3,  1.3),
  WaveParams(vec2f(-0.2, 0.95),  0.025, 4.2, 0.28, 1.5),
  // Very fine omnidirectional ripples
  WaveParams(vec2f(-0.5, -0.5),  0.025, 4.5, 0.3,  1.4),
  WaveParams(vec2f(0.4, 0.8),    0.015, 6.0, 0.25, 1.6),
  WaveParams(vec2f(0.85, -0.5),  0.012, 7.0, 0.2,  1.8),
  WaveParams(vec2f(-0.7, -0.7),  0.01,  8.5, 0.18, 2.0),
);

const WAVE_COUNT = 14;

struct GerstnerResult {
  pos: vec3f,
  normal: vec3f,
  jacobian: f32, // determinant of horizontal displacement Jacobian (< 1 = convergence)
};

// --- Procedural noise (shared by vertex + fragment) ---

fn hash2(p: vec2f) -> vec2f {
  let k = vec2f(0.3183099, 0.3678794);
  let pp = p * k + k.yx;
  return -1.0 + 2.0 * fract(16.0 * k * fract(pp.x * pp.y * (pp.x + pp.y)));
}

fn gradientNoise(p: vec2f) -> f32 {
  let i = floor(p);
  let f = fract(p);
  let u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(dot(hash2(i + vec2f(0.0, 0.0)), f - vec2f(0.0, 0.0)),
        dot(hash2(i + vec2f(1.0, 0.0)), f - vec2f(1.0, 0.0)), u.x),
    mix(dot(hash2(i + vec2f(0.0, 1.0)), f - vec2f(0.0, 1.0)),
        dot(hash2(i + vec2f(1.0, 1.0)), f - vec2f(1.0, 1.0)), u.x),
    u.y
  );
}

// Spatial amplitude variation — some areas calmer, some noticeably rougher
fn spatialVariation(pos: vec2f, t: f32) -> f32 {
  let p1 = pos * 0.004 + vec2f(t * 0.007, t * 0.005);
  let p2 = pos * 0.0015 + vec2f(t * 0.003, -t * 0.002);
  // Two octaves: broad swells of activity + local patches
  let n = gradientNoise(p1) * 0.6 + gradientNoise(p2) * 0.4;
  return 0.3 + 1.4 * clamp(n + 0.5, 0.0, 1.0);  // range ~0.3 to 1.7
}

fn gerstner(pos: vec2f, t: f32) -> GerstnerResult {
  var dp = vec3f(pos.x, 0.0, pos.y);
  var N = vec3f(0.0, 1.0, 0.0);
  var Jxx = 1.0; var Jxz = 0.0; var Jzx = 0.0; var Jzz = 1.0;

  // Spatial variation: areas of ocean have different wave intensity
  let spatial = spatialVariation(pos, t);

  for (var i = 0; i < WAVE_COUNT; i++) {
    let w = WAVES[i];
    let d = normalize(w.dir);

    // Wave group envelope: amplitude modulates at half the phase speed
    // Creates natural "sets of waves" that build up and calm down
    let groupPhase = dot(d, pos) * w.frequency * 0.12 + t * w.speed * 0.5;
    let envelope = 0.15 + 0.85 * (0.5 + 0.5 * sin(groupPhase));

    let amp = w.amplitude * envelope * spatial;

    let phase = dot(d, pos) * w.frequency + t * w.speed;
    let c = cos(phase);
    let s = sin(phase);

    // Displacement
    dp.x += w.steepness * amp * d.x * c;
    dp.y += amp * s;
    dp.z += w.steepness * amp * d.y * c;

    // Normal
    let wa = w.frequency * amp;
    N.x -= d.x * wa * c;
    N.z -= d.y * wa * c;
    N.y -= w.steepness * wa * s;

    // Jacobian accumulation
    let QwAc = w.steepness * w.frequency * amp * c;
    Jxx -= d.x * d.x * QwAc;
    Jxz -= d.x * d.y * QwAc;
    Jzx -= d.y * d.x * QwAc;
    Jzz -= d.y * d.y * QwAc;
  }

  // Height sharpening: pow() makes peaks sharper, troughs flatter (trochoidal look)
  let rawH = dp.y;
  let normH = clamp(rawH * 0.3 + 0.5, 0.0, 1.0); // rough normalize to [0,1]
  dp.y = (pow(normH, 1.6) - 0.5) / 0.3; // sharpen and map back

  let det = Jxx * Jzz - Jxz * Jzx;

  // Exaggerate normals where waves converge (Jacobian det < 1)
  let convergence = clamp(1.0 - det, 0.0, 1.0);
  let boost = 1.0 + convergence * 2.5;
  N.x *= boost;
  N.z *= boost;

  var r: GerstnerResult;
  r.pos = dp;
  r.normal = normalize(N);
  r.jacobian = det;
  return r;
}

struct VSOut {
  @builtin(position) clipPos: vec4f,
  @location(0) worldPos: vec3f,
  @location(1) normal: vec3f,
  @location(2) color: vec3f,
  @location(3) emissive: f32,
  @location(4) roughness: f32,
};
struct GBufferOut {
  @location(0) albedo: vec4f,
  @location(1) normal: vec4f,
};

@vertex fn vsOcean(@location(0) vp: vec3f, @location(1) vn: vec3f) -> VSOut {
  let flatPos = vec2f(vp.x, vp.z);
  let g = gerstner(flatPos, ocean.time);

  let waveHeight = g.pos.y;

  // Two-stop color ramp: deep → mid → crest (colors from uniforms)
  let heightFactor = clamp((waveHeight + 1.0) * 0.5, 0.0, 1.0);
  var waterColor = select(
    mix(ocean.deepColor, ocean.midColor, heightFactor * 2.0),
    mix(ocean.midColor, ocean.crestColor, (heightFactor - 0.5) * 2.0),
    heightFactor > 0.5
  );

  // Brighten at wave convergence points (where Jacobian < 1)
  let convergence = clamp(1.0 - g.jacobian, 0.0, 1.0);
  waterColor = mix(waterColor, ocean.crestColor * 1.5, convergence * 0.3);

  var o: VSOut;
  o.clipPos = ocean.viewProj * vec4f(g.pos, 1.0);
  o.worldPos = g.pos;
  o.normal = g.normal;
  o.color = waterColor;
  o.emissive = 0.0;
  o.roughness = 0.02;
  return o;
}

// --- Procedural normal detail (replaces normal maps) ---
// Directionally stretched gradient noise creates elongated wave-like ridges.
// Each octave is stretched along a different wave direction for organic overlap.

// Subtle organic fill — the Jacobian-boosted Gerstner normals handle the main ridges.
// This just adds irregular variation so the surface doesn't look perfectly smooth between ridges.

fn detailNormal(pos: vec2f, t: f32) -> vec2f {
  let eps = 0.1;
  var dn = vec2f(0.0);

  // Large-scale organic variation
  let uv1 = pos * 0.25 + vec2f(t * 0.02, t * 0.015);
  let n1x = gradientNoise(uv1 + vec2f(eps, 0.0)) - gradientNoise(uv1 - vec2f(eps, 0.0));
  let n1z = gradientNoise(uv1 + vec2f(0.0, eps)) - gradientNoise(uv1 - vec2f(0.0, eps));
  dn += vec2f(n1x, n1z) * 0.2;

  // Medium variation at different scroll direction
  let uv2 = pos * 0.7 + vec2f(-t * 0.025, t * 0.018);
  let n2x = gradientNoise(uv2 + vec2f(eps, 0.0)) - gradientNoise(uv2 - vec2f(eps, 0.0));
  let n2z = gradientNoise(uv2 + vec2f(0.0, eps)) - gradientNoise(uv2 - vec2f(0.0, eps));
  dn += vec2f(n2x, n2z) * 0.12;

  // Fine ripples — these create the micro-normals that produce natural sparkles
  let uv3 = pos * 2.5 + vec2f(t * 0.04, -t * 0.03);
  let n3x = gradientNoise(uv3 + vec2f(eps, 0.0)) - gradientNoise(uv3 - vec2f(eps, 0.0));
  let n3z = gradientNoise(uv3 + vec2f(0.0, eps)) - gradientNoise(uv3 - vec2f(0.0, eps));
  dn += vec2f(n3x, n3z) * 0.07;

  // Very fine ripples — high frequency sparkle detail
  let uv4 = pos * 6.0 + vec2f(-t * 0.06, t * 0.045);
  let n4x = gradientNoise(uv4 + vec2f(eps, 0.0)) - gradientNoise(uv4 - vec2f(eps, 0.0));
  let n4z = gradientNoise(uv4 + vec2f(0.0, eps)) - gradientNoise(uv4 - vec2f(0.0, eps));
  dn += vec2f(n4x, n4z) * 0.04;

  return dn;
}

@fragment fn fsOcean(in: VSOut) -> GBufferOut {
  var o: GBufferOut;

  // Procedural normal detail — perturb the smooth Gerstner normal
  let dn = detailNormal(in.worldPos.xz, ocean.time);
  let detailedN = normalize(in.normal + vec3f(dn.x, 0.0, dn.y));

  let waveHeight = clamp(in.worldPos.y * 0.5 + 0.5, 0.0, 1.0);
  o.albedo = vec4f(in.color, -waveHeight - 0.1); // negative = water flag, magnitude = height
  o.normal = vec4f(detailedN, in.roughness);
  return o;
}
`;

export class Ocean {
  private pipeline: GPURenderPipeline;
  private uniformBuffer: GPUBuffer;
  private bindGroup: GPUBindGroup;
  private vertexBuffer: GPUBuffer;
  private indexBuffer: GPUBuffer;
  private indexCount: number;
  private uniformData = new Float32Array(32);
  triangleCount = 0;

  constructor(private gpu: GPU) {
    const { device } = gpu;

    // Pipeline
    const module = device.createShaderModule({ code: OCEAN_SHADER });
    const uniformLayout = device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } }],
    });

    this.pipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [uniformLayout] }),
      vertex: { module, entryPoint: "vsOcean", buffers: [POS_NORM_LAYOUT] },
      fragment: { module, entryPoint: "fsOcean", targets: GBUFFER_TARGETS },
      primitive: { topology: "triangle-list" },
      depthStencil: { format: DEPTH_FORMAT, depthWriteEnabled: true, depthCompare: "less" },
    });

    this.uniformBuffer = device.createBuffer({ size: 128, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    this.bindGroup = device.createBindGroup({
      layout: uniformLayout,
      entries: [{ binding: 0, resource: { buffer: this.uniformBuffer } }],
    });

    // Generate mesh
    const { vb, ib, count } = this.generateMesh(device);
    this.vertexBuffer = vb;
    this.indexBuffer = ib;
    this.indexCount = count;
    this.triangleCount = count / 3;
  }

  private generateMesh(device: GPUDevice) {
    const verts = GRID_SIZE + 1;
    const vertCount = verts * verts;
    const quadCount = GRID_SIZE * GRID_SIZE;
    const indexCount = quadCount * 6;

    const vertexData = new Float32Array(vertCount * 6);
    const half = WORLD_SIZE / 2;
    const step = WORLD_SIZE / GRID_SIZE;

    for (let z = 0; z < verts; z++) {
      for (let x = 0; x < verts; x++) {
        const i = (z * verts + x) * 6;
        vertexData[i] = -half + x * step;
        vertexData[i + 1] = 0;
        vertexData[i + 2] = -half + z * step;
        vertexData[i + 3] = 0;
        vertexData[i + 4] = 1;
        vertexData[i + 5] = 0;
      }
    }

    const indexData = new Uint32Array(indexCount);
    let idx = 0;
    for (let z = 0; z < GRID_SIZE; z++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        const tl = z * verts + x;
        const tr = tl + 1;
        const bl = tl + verts;
        const br = bl + 1;
        indexData[idx++] = tl; indexData[idx++] = bl; indexData[idx++] = tr;
        indexData[idx++] = tr; indexData[idx++] = bl; indexData[idx++] = br;
      }
    }

    const vb = device.createBuffer({ size: vertexData.byteLength, usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST });
    device.queue.writeBuffer(vb, 0, vertexData);
    const ib = device.createBuffer({ size: indexData.byteLength, usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST });
    device.queue.writeBuffer(ib, 0, indexData);

    return { vb, ib, count: indexCount };
  }

  /** Ocean surface colors — tweakable at runtime */
  deepColor: [number, number, number] = [0.005, 0.03, 0.04];
  midColor: [number, number, number] = [0.01, 0.08, 0.06];
  crestColor: [number, number, number] = [0.02, 0.16, 0.10];

  /** Call during a G-buffer render pass */
  draw(pass: GPURenderPassEncoder, viewProj: Float32Array, time: number): void {
    const data = this.uniformData;
    data.set(viewProj);
    data[16] = time;
    // Colors
    data[20] = this.deepColor[0]; data[21] = this.deepColor[1]; data[22] = this.deepColor[2];
    data[24] = this.midColor[0]; data[25] = this.midColor[1]; data[26] = this.midColor[2];
    data[28] = this.crestColor[0]; data[29] = this.crestColor[1]; data[30] = this.crestColor[2];
    this.gpu.device.queue.writeBuffer(this.uniformBuffer, 0, data);

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.setVertexBuffer(0, this.vertexBuffer);
    pass.setIndexBuffer(this.indexBuffer, "uint32");
    pass.drawIndexed(this.indexCount);
  }
}
